import { ChartGroupBoxPlotApi } from "src/api/generated/spc/controlChart/api/chartGroupBoxPlotApi.ts";
import { AsyncFetchRender } from "src/components/common/async/AsyncFetchRender.tsx";
import { AavoECharts } from "src/components/common/echarts/AavoECharts.tsx";
import { formatNumber } from "src/utils/numberUtils.ts";
import { useTheme } from "@mui/material";
import { Typography } from "@mui/material";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import i18n from "i18next";

export interface ControlChartGroupBoxPlotViewProps {
	chartGroupId: number;
	recordCount: number;
	displayTitle?: string;
}

export function ControlChartGroupBoxPlotView({
	chartGroupId,
	recordCount,
	displayTitle,
}: ControlChartGroupBoxPlotViewProps) {
	const { palette } = useTheme();
	return (
		<VerticalBox sx={{ flex: 1, minHeight: 0 }}>
			{displayTitle != null && <Typography variant={"h6"}>{displayTitle}</Typography>}
			<AsyncFetchRender
				fetch={() => ChartGroupBoxPlotApi.getChartGroupBoxPlot({ chartGroupId, recordCount })}
				content={(data) => (
					<AavoECharts
						sx={{ flex: 1, minHeight: 400 }}
						option={{
							animation: false,
							grid: { containLabel: true, left: 20, right: 20, top: 20, bottom: 30 },
							xAxis: {
								type: "category",
								data: data.controlCharts.map((chart) => chart.controlChartName),
							},
							yAxis: { type: "value" },
							tooltip: {
								trigger: "item",
								formatter: ({ dataIndex }: { dataIndex: number }) => {
									const chart = data.controlCharts[dataIndex];
									if (chart == null) return "";
									return [
										`<b>${escapeHtml(chart.controlChartName)}</b>`,
										`${i18n.t("count")}: ${chart.count}`,
										`${i18n.t("maximum")}: ${formatNullable(chart.high)}`,
										`${i18n.t("third_quartile")}: ${formatNullable(chart.q3)}`,
										`${i18n.t("median")}: ${formatNullable(chart.median)}`,
										`${i18n.t("first_quartile")}: ${formatNullable(chart.q1)}`,
										`${i18n.t("minimum")}: ${formatNullable(chart.low)}`,
									].join("<br/>");
								},
							},
							series: [
								{
									type: "boxplot",
									data: data.controlCharts.map((chart) => [
										chart.low,
										chart.q1,
										chart.median,
										chart.q3,
										chart.high,
									]),
									markLine:
										data.taktTime == null ?
											undefined
										:	{
												symbol: "none",
												data: [
													{
														yAxis: data.taktTime,
														label: {
															formatter: `${i18n.t("takt_time")}: ${formatNumber(data.taktTime)}`,
														},
														lineStyle: { color: palette.error.main, width: 2 },
													},
												],
											},
								},
							],
						}}
					/>
				)}
			/>
		</VerticalBox>
	);
}

function formatNullable(value: number | null | undefined): string {
	return value == null ? "-" : formatNumber(value);
}

function escapeHtml(value: string): string {
	return value.replace(/[&<>'"]/g, (character) => {
		return {
			"&": "&amp;",
			"<": "&lt;",
			">": "&gt;",
			"'": "&#39;",
			'"': "&quot;",
		}[character]!;
	});
}
